<template>
<div class='widget-W0154V0001'>
  <div class="news-widget">
    <div v-if="loading" class="news__loading" aria-live="polite">
      <i class="fa fa-spinner" aria-hidden="true"></i>
    </div>
    <div v-else-if="error" class="news__error" aria-live="assertive">
      <p>{{ error }}</p>
    </div>
    <div v-else class="news__container">
      <a v-for="item in newsItems" :key="item.$id" :href="item.$url" target="_blank" rel="noopener" class="news__card"
         :aria-label="'Read more about ' + item.title">
        <div class="news__card-image" v-if="item.owcmpageimage_data">
          <transition name="fade">
            <img v-if="item.owcmpageimage_data" class="news__card-image-img"
                 :src="`data:image/png;base64,${item.owcmpageimage_data}`"
                 :alt="item.title"></img>
            <div v-else class="news__imagePlaceholder">
              <svg class="news__placeholder-icon" width="48" height="48" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>
          </transition>
        </div>
        <div class="news__card-content">
          <ul class="news__card-tags" v-if="item.tags && item.tags.length > 0" role="list">
            <li v-for="tag in item.tags.slice(0, 2)" :key="tag" class="news__card-tag">{{ tag.name }}</li>
            <li v-if="item.tags.length > 2" class="news__card-tag-more"
                :aria-label="'Additional tags: ' + item.tags.slice(2).map(t => t.name).join(', ')"
                :title="item.tags.slice(2).map(t => t.name).join(', ')">+{{ item.tags.length - 2 }}
            </li>
          </ul>
          <div class="news__card-heading">
            <h3 class="news__card-title">{{ item.title }}</h3>
          </div>
          <div class="news__card-details">
                        <span class="news__card-date" v-if="item.owcmarticledate">
                            {{ formatDate(item.owcmarticledate) }}
                        </span>
            <span class="news__card-author" v-if="getAuthorName(item)">
                           - {{ getAuthorName(item) }}
                        </span>
          </div>
          <div class="news__card-summary">
            <widget-html :html="item.owcmpagesummary"></widget-html>
          </div>
        </div>
      </a>
      <div v-if="hasMoreLink" class="news__footer">
        <a :href="moreLinkUrl" target="_blank" rel="noopener" class="news__more-button">
          {{ moreLinkText }}
        </a>
      </div>
    </div>
  </div>
</div>
</template>

<script>
export default {
  props: {
    axios: {
      type: Function,
      required: true
    },
    moment: {
      type: Function,
      required: true
    },
    placement: {
      type: String,
      required: true
    },
    isMobileApp: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      newsItems: [],
      loading: true,
      error: null,
      omniaApiBaseUrl: "https://b4ae8c32fd95410ba388365d09a3b437-aalborg.omniacloud.eu",
      moreLinkUrl: '',
      moreLinkText: '',
    };
  },
  computed: {
    hasMoreLink: function () {
      return Boolean(this.moreLinkText && this.isValidMoreLinkUrl(this.moreLinkUrl));
    }
  },
  methods: {
    fetchNews: async function () {
      try {
        const response = await this.axios.get(this.omniaApiBaseUrl + '/api/widget/settings');
        if (response.data.success) {
          const widgetSettings = response.data.data;

          if (!widgetSettings) return;

          this.newsItems = Array.isArray(widgetSettings.newsItems) ? widgetSettings.newsItems : [];
          this.moreLinkUrl = widgetSettings.moreLinkUrl ? widgetSettings.moreLinkUrl : '';
          this.moreLinkText = widgetSettings.moreLinkText ? widgetSettings.moreLinkText : '';
        }
      } catch (err) {
        console.error("Failed to get news from Omnia. More Details: ", err);
        this.error = "Failed to get news from Omnia";
      }
    },

    formatDate: function (dateString) {
      if (!dateString) return '';
      return this.moment(dateString).format('DD.MM.YYYY').toUpperCase();
    },

    isValidMoreLinkUrl: function (url) {
      if (!url) return false;

      try {
        const parsedUrl = new URL(url);
        return true;
      } catch (err) {
        return false;
      }
    },

    getAuthorName: function (item) {
      if (!item.owcmpagecontact || item.owcmpagecontact.length === 0) return '';
      return item.owcmpagecontact[0].displayName;
    }
  },
  mounted: async function () {
    this.loading = true;
    await this.fetchNews();
    this.loading = false;
  }
};
</script>

<style lang="scss">
.widget-W0154V0001 {
.news-widget {
  box-sizing: border-box;
  container-type: inline-size;
}

.news__loading, .news__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #4a4a4a;
  font-size: 0.95rem;
  font-weight: 500;
}

.news__error {
  color: #B50050;
}

.news__container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.news__card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 254px;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;
}

.news__card:focus {
  outline: 2px solid #007A8D;
  outline-offset: 2px;
}

.news__card-image {
  position: relative;
  flex: 0 0 40%;
  height: 100%;
  background-color: #e5e7eb;
  overflow: hidden;
}

.news__card-image-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news__imagePlaceholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e5e7eb;
  color: #9ca3af;
}

.news__placeholder-icon {
  opacity: 0.5;
}

.news__card-content {
  padding: 12px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  justify-content: flex-start;
  min-width: 0;
  align-items: flex-start;
}

.news__card-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 10px;
  padding-left: 0;
  margin-bottom: 4px;
  background: transparent;
  text-align: left;
}

.news__card-heading h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.3;
  color: #222350;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news__card-details {
  font-size: 0.85rem;
  color: #222350;
  font-weight: 600;
}

.news__card-date {
  letter-spacing: 0.05em;
}

.news__card-author {
  /* additional styles if needed */
}

.news__card-summary {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #222350;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news__card-summary p {
  margin: 0;
}

.news__card-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.news__card-tag {
  display: inline-block;
  background-color: #222350;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.news__card-tag-more {
  display: inline-block;
  background-color: #222350;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: help;
}

.news__footer {
  display: flex;
  justify-content: flex-start;
  margin: 4px 0 0;
}

.news__more-button {
  background-color: #222350;
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
  min-height: 44px;
  padding: 12px 20px;
  text-decoration: none;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.news__more-button:hover,
.news__more-button:focus {
  background-color: #4A4DA0;
  transform: translateY(-1px);
}

.news__more-button:focus {
  outline: 2px solid #4A4DA0;
  outline-offset: 2px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

@container (max-width: 768px) {
  .news__card-content {
    padding: 12px 24px !important;
    gap: 12px !important;
  }

  .news__card-heading {
    margin-bottom: 8px !important;
    padding-bottom: 12px !important;
  }

  .news__card-summary {
    line-clamp: 2 !important;
    -webkit-line-clamp: 2 !important;
  }
}

@container (max-width: 700px) {
  .news__card-image {
    display: none !important;
  }
}

@container (max-width: 425px) {
  .news__container {
    gap: 10px !important;
  }

  .news__card {
    height: auto !important;
  }

  .news__card-tags {
    display: none !important;
  }

  .news__card-author {
    display: none !important;
  }

  .news__card-summary {
    display: none !important;
  }
}
}
</style>
